import { Component, EventEmitter, Output, OnInit, ChangeDetectionStrategy, AfterViewInit, OnDestroy, Optional } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { Inject } from '@angular/core';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { Subject } from 'rxjs';
import { GoDialogRef } from '@go-modules/services/go-dialog-ref/go-dialog-ref';

interface NavigationItem {
	name: string;
	route: string;
	translationKey: string;
	params?: Record<string, any>;
}

@Component({
	selector: 'activity-navigation',
	imports: [
		TranslateModule
	],
	templateUrl: './activity-navigation.component.html',
	styleUrls: ['./activity-navigation.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivityNavigationComponent implements OnInit, AfterViewInit, OnDestroy {
	@Output() public navigationSelected = new EventEmitter<void>();
	public navigationItems: NavigationItem[] = [
		{
			name: 'Submissions',
			route: ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK,
			translationKey: 'common_submissions',
			params: { type: 'evaluate' }
		},
		{
			name: 'Task Report',
			route: ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK_REPORT,
			translationKey: 'activity-view_navigation-task-report'
		},
		{
			name: 'Activity Report',
			route: ROUTE_NAMES.DASHBOARD_ACTIVITY_REPORT,
			translationKey: 'activity-report_header-title'
		}
	];
	private destroy$ = new Subject<void>();
	public transitionHookDeregister?: (() => void) | undefined;
	public selectedRoute: string | null = null;

	constructor (
		public activityService: NgxActivityService,
		private routerService: RouterService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Optional() private dialogRef: GoDialogRef
	) {}

	public ngOnInit (): void {
		const currentRoute = this.routerService.getCurrentRouteName();
		if (currentRoute) {
			this.selectedRoute = currentRoute;
		}
	}

	public ngOnDestroy (): void {
		if (this.transitionHookDeregister) {
			this.transitionHookDeregister();
		}
		this.destroy$.next();
		this.destroy$.complete();
	}

	public ngAfterViewInit (): void {
		this.navigationSelected.emit();
	}

	public isActiveRoute (navigationItem: NavigationItem): boolean {
		const currentRoute = this.routerService.getCurrentRouteName();
		const currentParams = this.routerService.getCurrentParams();


		// Route must match
		if (currentRoute !== navigationItem.route) {
			return false;
		}

		// If navigation item has params, check if current params match
		if (navigationItem.params) {
			for (const [key, value] of Object.entries(navigationItem.params)) {
				if (currentParams[key] !== value) {
					return false;
				}
			}
		}

		return true;
	}

	public onNavigationClick (navigationItem: NavigationItem): void {
		this.navigationSelected.emit();
		this.selectedRoute = navigationItem.route;

		const activity = this.selectedService.getActivity();
		if (activity) {
			this.routerService.navigateTo(navigationItem.route as ROUTE_NAMES, {
				activity_id: activity.activity_id,
				mode: null, // Clear edit mode when navigating via navigation items
				...navigationItem.params
			});
		}

		// Close sidepanel if opened in one
		if (this.dialogRef) {
			this.dialogRef.close();
		}
	}
}
